import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { principles, stats, testimonials } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionHeading from '@/components/base/SectionHeading';
import usePageMeta from '@/hooks/usePageMeta';

const philosophy = [
  {
    step: '01',
    title: '从投稿开始',
    desc: '每天都有同学把身边发生的事写给我们。我们先做的不是发稿，而是把时间、地点、经过一条条对齐。',
  },
  {
    step: '02',
    title: '交叉核实',
    desc: '同一件事至少要有两个独立来源可以互相印证。只有一方说法时，我们会明确标注「待核实」，不当作定论。',
  },
  {
    step: '03',
    title: '梳理脉络',
    desc: '把零散信息整理成时间线，标出每一个关键节点，让读者三分钟就能看懂一件事的前因后果。',
  },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于高校吃瓜社',
      inLanguage: 'zh-CN',
      description:
        '高校吃瓜社是一个聚焦校园公共话题的围观社区，坚持就事论事、来源可溯、多方视角与保护隐私。',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: '/about' },
        ],
      },
      publisher: {
        '@type': 'Organization',
        name: '高校吃瓜社',
        email: 'contact@gaoxiaochigua.com',
      },
    }),
    []
  );

  usePageMeta({
    title: '关于我们｜高校吃瓜社：围观校园热门现场，吃瓜不迷路',
    description:
      '了解高校吃瓜社的网站定位、发展理念与内容原则。我们聚焦高校吃瓜事件与热点，坚持就事论事、来源可溯、多方视角、保护隐私，让每一次围观都有依据。',
    keywords: '高校吃瓜,高校吃瓜社,关于我们,内容原则,高校吃瓜新闻',
    canonicalPath: '/about',
    ogType: 'website',
    jsonLd,
  });

  return (
    <div>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20editorial%20illustration%20of%20university%20students%20chatting%20on%20a%20green%20campus%20lawn%20with%20coffee%20and%20notebooks%2C%20coral%20and%20amber%20tones%2C%20cozy%20friendly%20atmosphere%2C%20soft%20cream%20background%2C%20modern%20flat%20style%2C%20no%20text&width=1400&height=800&seq=about-campus-01&orientation=landscape"
            alt="高校吃瓜社 关于我们 校园话题讨论插画"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/75 to-foreground-950/45"></div>
        </div>
        <div className="relative mx-auto w-full px-4 py-12 md:px-6 md:py-20 lg:px-10">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3.5 py-1.5 text-xs font-semibold text-foreground-950">
              <i className="ri-team-line text-base leading-none" aria-hidden="true"></i>
              由一群爱记录的同学发起
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              关于高校吃瓜社
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-background-100 md:text-base">
              我们是一个专注校园公共话题的内容社区。这里不追热点噱头，只想把每一件被围观的事，认真整理成看得懂、经得起回看的样子。
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-background-100/25 pt-6 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <dd className="font-heading text-xl font-black text-accent-300 md:text-2xl">{item.value}</dd>
                  <dt className="mt-1 text-xs text-background-200 md:text-sm">{item.label}</dt>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-primary-600">
                <span className="h-1 w-6 rounded-full bg-primary-500" aria-hidden="true"></span>
                网站定位
              </p>
              <h2 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
                不是八卦集散地，而是校园话题的整理者
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                校园里每天都有值得讨论的事：食堂的新窗口、突然卡住的选课系统、宿舍楼下的小猫、毕业季的告别。这些事散落在各个群里，很快就沉下去了。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                高校吃瓜社想做的，是给它们一个可以回看的地方。我们把公开信息与同学投稿整理成结构化的事件条目，配上清晰的时间线，让后来的人也能看懂当时发生了什么。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/events"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览事件合集
                  <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/#submit"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-foreground-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-send-plane-line text-base leading-none" aria-hidden="true"></i>
                  投稿爆料
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 h-52 w-full overflow-hidden rounded-lg bg-background-200 md:h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20editorial%20photo%20of%20university%20students%20gathering%20around%20a%20table%20with%20laptops%20and%20notes%2C%20collaborative%20friendly%20atmosphere%2C%20soft%20natural%20light%2C%20coral%20amber%20tones%2C%20cream%20background%2C%20no%20text&width=1000&height=700&seq=about-position-01&orientation=landscape"
                  alt="同学们一起整理校园话题资料"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="h-40 w-full overflow-hidden rounded-lg bg-background-200 md:h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20close%20up%20of%20a%20notebook%20with%20handwritten%20timeline%20notes%20and%20a%20cup%20of%20coffee%20on%20a%20wooden%20desk%2C%20cozy%20study%20scene%2C%20coral%20amber%20tones%2C%20soft%20natural%20light%2C%20no%20text&width=600&height=600&seq=about-position-02&orientation=squarish"
                  alt="整理事件时间线的手写笔记"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="flex h-40 w-full flex-col justify-center rounded-lg bg-accent-50 p-5 md:h-48">
                <span className="font-heading text-3xl font-black text-primary-600">3 天</span>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  投稿到初步核对完成的平均时间，信息完整会更快。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
          <SectionHeading
            eyebrow="发展理念"
            title="一件事，我们是怎么一步步整理的"
            desc="没有捷径，只有把每条信息都落到具体的时间和来源上。"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {philosophy.map((item) => (
              <div key={item.step} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="font-heading text-3xl font-black text-background-400">{item.step}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="principles" className="scroll-mt-24 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
          <SectionHeading
            eyebrow="内容原则"
            title="四条底线，写进每一次更新里"
            desc="围观可以轻松，但整理内容这件事必须认真。以下原则适用于站内所有事件。"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div key={item.title} className="flex gap-4 rounded-lg bg-background-100 p-6">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl leading-none`} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 md:text-lg">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
          <SectionHeading
            eyebrow="同学口碑"
            title="来自不同高校的真实反馈"
            desc="他们既是读者，也是内容的提供者。"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-background-200">
                  <img
                    src={item.avatar}
                    alt={`${item.name} 头像`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <div>
                  <blockquote className="text-sm leading-relaxed text-foreground-700">{item.content}</blockquote>
                  <figcaption className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <strong className="text-sm font-semibold text-foreground-950">{item.name}</strong>
                    <span className="text-xs text-foreground-500">{item.role}</span>
                  </figcaption>
                </div>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="scroll-mt-24 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-8 rounded-lg bg-foreground-950 p-6 md:p-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <p className="text-xs font-semibold tracking-[0.24em] text-accent-400">联系我们</p>
              <h2 className="mt-3 font-heading text-2xl font-black leading-tight text-background-50 md:text-3xl">
                有内容建议、合作想法，或者只是想聊聊
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
                欢迎通过邮箱联系我们，也可以在投稿里写下你的想法。每一封认真写来的信，我们都会看。
              </p>
              <a
                href="mailto:contact@gaoxiaochigua.com"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-send-line text-base leading-none" aria-hidden="true"></i>
                contact@gaoxiaochigua.com
              </a>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg bg-secondary-900 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-800 text-accent-400">
                  <i className="ri-mail-line text-lg leading-none" aria-hidden="true"></i>
                </span>
                <h3 className="mt-3.5 font-heading text-base font-bold text-background-50">邮件联系</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-background-300">
                  内容更正、合作洽谈、授权转载，都可以发到这里。
                </p>
              </div>
              <div className="rounded-lg bg-secondary-900 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-800 text-accent-400">
                  <i className="ri-send-plane-line text-lg leading-none" aria-hidden="true"></i>
                </span>
                <h3 className="mt-3.5 font-heading text-base font-bold text-background-50">投稿爆料</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-background-300">
                  有事件线索或后续进展，直接用站内投稿表单告诉我们。
                </p>
                <Link
                  to="/#submit"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-300 hover:text-accent-200"
                >
                  前往投稿
                  <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}